import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, useLanguage } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Users, MessageSquare, Video, Send, Mic, MicOff, ArrowLeft,
  Plus, UserPlus, Crown, Trash2, Settings, Search, Play, Square, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const StudyGroupsPage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loading, setLoading] = useState(true);

  const l = language === 'fr' ? {
    title: "Groupes d'Etude",
    subtitle: "Vos groupes de travail et discussions",
    noGroups: "Vous n'appartenez a aucun groupe",
    members: "membres",
    openChat: "Ouvrir le chat",
    visio: "Visioconference",
    back: "Retour",
    lastMessage: "Dernier message",
  } : {
    title: "Study Groups",
    subtitle: "Your work groups and discussions",
    noGroups: "You don't belong to any group",
    members: "members",
    openChat: "Open chat",
    visio: "Video conference",
    back: "Back",
    lastMessage: "Last message",
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/study-groups/my`);
      setGroups(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (selectedGroup) {
    return (
      <GroupChat
        group={selectedGroup}
        onBack={() => { setSelectedGroup(null); fetchGroups(); }}
        language={language}
      />
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="study-groups-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900" data-testid="study-groups-title">{l.title}</h1>
        <p className="text-sm text-gray-500 mt-1">{l.subtitle}</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1,2].map(i => (
            <Card key={i} className="animate-pulse"><CardContent className="p-6"><div className="h-20 bg-gray-100 rounded" /></CardContent></Card>
          ))}
        </div>
      ) : groups.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">{l.noGroups}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {groups.map(group => (
            <Card
              key={group.id}
              className="card-hover cursor-pointer"
              onClick={() => setSelectedGroup(group)}
              data-testid={`group-card-${group.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-[var(--tenant-primary)]/10 flex items-center justify-center">
                      <Users className="w-5 h-5 text-[var(--tenant-primary)]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{group.name}</h3>
                      <p className="text-xs text-gray-500">
                        {group.promotion_label} - {group.academic_year}
                      </p>
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {group.member_count} {l.members}
                  </Badge>
                </div>
                {group.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{group.description}</p>
                )}
                <div className="flex items-center justify-between">
                  {group.last_message ? (
                    <p className="text-xs text-gray-400 truncate max-w-[200px]">
                      {group.last_message.type === 'audio' ? '🎵 Audio' : group.last_message.content}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-400">{l.lastMessage}: --</p>
                  )}
                  <div className="flex gap-2">
                    {group.visio_link && (
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                        onClick={(e) => { e.stopPropagation(); window.open(group.visio_link, '_blank'); }}>
                        <Video className="w-3 h-3" /> {l.visio}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ GROUP CHAT COMPONENT ============
const GroupChat = ({ group, onBack, language }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [groupDetail, setGroupDetail] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [showMembers, setShowMembers] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const pollIntervalRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const MAX_RECORDING_SECONDS = 120; // 2 minutes max

  const l = language === 'fr' ? {
    typeMessage: "Ecrivez votre message...",
    send: "Envoyer",
    record: "Maintenir pour enregistrer",
    recording: "Enregistrement...",
    members: "Membres",
    addMembers: "Ajouter des membres",
    chief: "Chef de groupe",
    setAsChief: "Nommer chef",
    remove: "Retirer",
    visio: "Visioconference",
    noMessages: "Aucun message. Commencez la conversation !",
    audioMessage: "Message audio",
    you: "Vous",
    back: "Retour",
    settings: "Parametres",
  } : {
    typeMessage: "Type your message...",
    send: "Send",
    record: "Hold to record",
    recording: "Recording...",
    members: "Members",
    addMembers: "Add members",
    chief: "Group chief",
    setAsChief: "Set as chief",
    remove: "Remove",
    visio: "Video conference",
    noMessages: "No messages. Start the conversation!",
    audioMessage: "Audio message",
    you: "You",
    back: "Back",
    settings: "Settings",
  };

  useEffect(() => {
    loadGroupDetail();
    loadMessages();
    // Poll for new messages every 5s
    pollIntervalRef.current = setInterval(loadMessages, 5000);
    return () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); };
  }, [group.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadGroupDetail = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/study-groups/${group.id}`);
      setGroupDetail(res.data);
    } catch (err) { console.error(err); }
  };

  const loadMessages = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/study-groups/${group.id}/messages`);
      setMessages(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const sendTextMessage = async () => {
    if (!newMessage.trim()) return;
    try {
      await axios.post(`${API_URL}/api/study-groups/${group.id}/messages`, {
        content: newMessage, type: "text"
      });
      setNewMessage('');
      loadMessages();
    } catch (err) {
      toast.error(language === 'fr' ? 'Erreur envoi' : 'Send error');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendTextMessage();
    }
  };

  // Audio Recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      setRecordingTime(0);

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setRecordingTime(0);
        await sendAudioMessage(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);

      // Timer: auto-stop at 2 minutes
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const next = prev + 1;
          if (next >= MAX_RECORDING_SECONDS) {
            stopRecording();
            return 0;
          }
          return next;
        });
      }, 1000);
    } catch (err) {
      toast.error(language === 'fr' ? 'Acces micro refuse' : 'Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  };

  const formatRecordTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const sendAudioMessage = async (blob) => {
    const formData = new FormData();
    formData.append('audio', blob, 'voice_message.webm');
    try {
      await axios.post(`${API_URL}/api/study-groups/${group.id}/messages/audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      loadMessages();
    } catch (err) {
      toast.error(language === 'fr' ? 'Erreur envoi audio' : 'Audio send error');
    }
  };

  const isStaff = ['super_admin', 'rector', 'dean', 'department_head', 'admin_staff', 'teacher'].includes(user?.role);
  const isChief = groupDetail?.chief_id === user?.id;
  const canManage = isStaff || isChief;

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="flex flex-col h-[calc(100vh-10rem)]" data-testid="group-chat">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack} data-testid="chat-back-button">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-10 h-10 rounded-xl bg-[var(--tenant-primary)]/10 flex items-center justify-center">
            <Users className="w-5 h-5 text-[var(--tenant-primary)]" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">{group.name}</h2>
            <p className="text-xs text-gray-500">
              {groupDetail?.member_details?.length || group.member_count} {l.members}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {group.visio_link && (
            <Button size="sm" variant="outline" onClick={() => window.open(group.visio_link, '_blank')} data-testid="visio-button">
              <Video className="w-4 h-4 mr-1" /> {l.visio}
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setShowMembers(!showMembers)} data-testid="members-button">
            <Users className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden mt-2">
        {/* Messages */}
        <div className={`flex-1 flex flex-col ${showMembers ? 'hidden md:flex' : ''}`}>
          <ScrollArea className="flex-1 pr-2">
            <div className="space-y-3 py-2">
              {loading ? (
                <div className="text-center py-8 text-gray-400">{language === 'fr' ? 'Chargement...' : 'Loading...'}</div>
              ) : messages.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <MessageSquare className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">{l.noMessages}</p>
                </div>
              ) : messages.map(msg => {
                const isMine = msg.sender_id === user?.id;
                return (
                  <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`} data-testid={`message-${msg.id}`}>
                    <div className={`max-w-[75%] ${isMine ? 'order-1' : ''}`}>
                      {!isMine && (
                        <p className="text-xs text-gray-500 mb-1 ml-1">{msg.sender_name}</p>
                      )}
                      <div className={`rounded-2xl px-4 py-2.5 ${
                        isMine
                          ? 'bg-[var(--tenant-primary)] text-white rounded-br-md'
                          : 'bg-gray-100 text-gray-900 rounded-bl-md'
                      }`}>
                        {msg.type === 'audio' ? (
                          <AudioPlayer audioId={msg.audio_url} isMine={isMine} />
                        ) : (
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        )}
                      </div>
                      <p className={`text-[10px] text-gray-400 mt-0.5 ${isMine ? 'text-right mr-1' : 'ml-1'}`}>
                        {formatTime(msg.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Input */}
          <div className="pt-3 border-t mt-2">
            {isRecording ? (
              <div className="flex items-center gap-3 p-2 bg-red-50 rounded-xl" data-testid="recording-bar">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium text-red-700">{l.recording}</span>
                <span className="text-xs text-red-500 font-mono">{formatRecordTime(recordingTime)} / 2:00</span>
                <div className="flex-1 h-1.5 bg-red-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-red-500 transition-all duration-1000"
                    style={{ width: `${(recordingTime / MAX_RECORDING_SECONDS) * 100}%` }}
                  />
                </div>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={stopRecording}
                  className="gap-1"
                  data-testid="stop-recording-button"
                >
                  <Square className="w-3 h-3" />
                  {language === 'fr' ? 'Arreter' : 'Stop'}
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={l.typeMessage}
                  className="flex-1"
                  data-testid="message-input"
                />
                <Button
                  size="icon"
                  variant="outline"
                  onClick={startRecording}
                  data-testid="record-button"
                  title={language === 'fr' ? 'Enregistrer un audio (max 2 min)' : 'Record audio (max 2 min)'}
                >
                  <Mic className="w-4 h-4" />
                </Button>
                <Button size="icon" onClick={sendTextMessage} disabled={!newMessage.trim()} data-testid="send-message-button">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Members Panel */}
        {showMembers && (
          <MembersPanel
            groupDetail={groupDetail}
            canManage={canManage}
            language={language}
            onClose={() => setShowMembers(false)}
            onRefresh={loadGroupDetail}
            l={l}
          />
        )}
      </div>
    </div>
  );
};

// ============ AUDIO PLAYER ============
const AudioPlayer = ({ audioId, isMine }) => {
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const togglePlay = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(`${API_URL}/api/files/${audioId}`);
      audioRef.current.onloadedmetadata = () => {
        if (audioRef.current.duration && isFinite(audioRef.current.duration)) {
          setDuration(audioRef.current.duration);
        }
      };
      audioRef.current.onended = () => {
        setPlaying(false);
        setCurrentTime(0);
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
      if (intervalRef.current) clearInterval(intervalRef.current);
    } else {
      audioRef.current.play().catch(() => {});
      setPlaying(true);
      intervalRef.current = setInterval(() => {
        if (audioRef.current) setCurrentTime(audioRef.current.currentTime);
      }, 200);
    }
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <button onClick={togglePlay} className="flex items-center gap-2 min-w-[150px] py-1" data-testid="audio-play-button">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
        isMine ? 'bg-white/20' : 'bg-gray-200'
      }`}>
        {playing ? <Square className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
      </div>
      <div className="flex-1 min-w-[60px]">
        <div className="h-1.5 rounded-full bg-current opacity-20 relative overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-current opacity-80 transition-all"
            style={{ width: `${playing ? progress : 0}%` }}
          />
        </div>
        <div className="flex justify-between mt-0.5">
          <span className="text-[10px] opacity-60">{playing ? formatTime(currentTime) : '0:00'}</span>
          {duration > 0 && <span className="text-[10px] opacity-60">{formatTime(duration)}</span>}
        </div>
      </div>
    </button>
  );
};

// ============ MEMBERS PANEL ============
const MembersPanel = ({ groupDetail, canManage, language, onClose, onRefresh, l }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSetChief = async (userId) => {
    try {
      await axios.put(`${API_URL}/api/study-groups/${groupDetail.id}`, { chief_id: userId });
      toast.success(language === 'fr' ? 'Chef de groupe nomme' : 'Group chief assigned');
      onRefresh();
    } catch (err) { toast.error('Erreur'); }
  };

  const handleRemoveMember = async (userId) => {
    try {
      await axios.delete(`${API_URL}/api/study-groups/${groupDetail.id}/members/${userId}`);
      toast.success(language === 'fr' ? 'Membre retire' : 'Member removed');
      onRefresh();
    } catch (err) { toast.error('Erreur'); }
  };

  const handleAddMembers = async (userIds) => {
    try {
      await axios.post(`${API_URL}/api/study-groups/${groupDetail.id}/members`, { user_ids: userIds });
      toast.success(language === 'fr' ? 'Membres ajoutes' : 'Members added');
      onRefresh();
      setShowAddModal(false);
    } catch (err) { toast.error('Erreur'); }
  };

  const members = groupDetail?.member_details || [];
  const availableStudents = (groupDetail?.available_students || []).filter(
    s => !s.already_member && (
      !searchQuery ||
      `${s.first_name} ${s.last_name}`.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  return (
    <div className="w-full md:w-72 md:ml-4 border-l md:pl-4 flex flex-col" data-testid="members-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm">{l.members} ({members.length})</h3>
        <div className="flex gap-1">
          {canManage && (
            <Button size="sm" variant="ghost" onClick={() => setShowAddModal(true)} data-testid="add-members-button">
              <UserPlus className="w-4 h-4" />
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onClose} className="md:hidden">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="space-y-2">
          {members.map(member => (
            <div key={member.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50" data-testid={`member-${member.id}`}>
              <div className="flex items-center gap-2">
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="text-xs bg-[var(--tenant-primary)]/10 text-[var(--tenant-primary)]">
                    {member.first_name?.[0]}{member.last_name?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-medium">{member.first_name} {member.last_name}</p>
                  {member.is_chief && (
                    <div className="flex items-center gap-1">
                      <Crown className="w-3 h-3 text-[var(--tenant-secondary)]" />
                      <span className="text-[10px] text-[var(--tenant-secondary)]">{l.chief}</span>
                    </div>
                  )}
                </div>
              </div>
              {canManage && !member.is_chief && (
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleSetChief(member.id)} title={l.setAsChief}>
                    <Crown className="w-3 h-3" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500" onClick={() => handleRemoveMember(member.id)} title={l.remove}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* Add Members Dialog */}
      {showAddModal && (
        <AddMembersDialog
          availableStudents={availableStudents}
          searchQuery={searchQuery}
          onSearch={setSearchQuery}
          onAdd={handleAddMembers}
          onClose={() => setShowAddModal(false)}
          language={language}
        />
      )}
    </div>
  );
};

// ============ ADD MEMBERS DIALOG ============
const AddMembersDialog = ({ availableStudents, searchQuery, onSearch, onAdd, onClose, language }) => {
  const [selected, setSelected] = useState([]);

  const toggle = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{language === 'fr' ? 'Ajouter des membres' : 'Add members'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => onSearch(e.target.value)}
              placeholder={language === 'fr' ? 'Rechercher...' : 'Search...'}
              className="pl-10"
              data-testid="search-students-input"
            />
          </div>
          <ScrollArea className="h-64">
            <div className="space-y-1">
              {availableStudents.map(s => (
                <div
                  key={s.id}
                  className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                    selected.includes(s.id) ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => toggle(s.id)}
                  data-testid={`add-student-${s.id}`}
                >
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="text-xs">{s.first_name?.[0]}{s.last_name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{s.first_name} {s.last_name}</p>
                    <p className="text-xs text-gray-500">{s.matricule}</p>
                  </div>
                  {selected.includes(s.id) && (
                    <Badge className="bg-blue-100 text-blue-700 text-xs">
                      {language === 'fr' ? 'Selectionne' : 'Selected'}
                    </Badge>
                  )}
                </div>
              ))}
              {availableStudents.length === 0 && (
                <p className="text-center text-sm text-gray-400 py-4">
                  {language === 'fr' ? 'Aucun etudiant disponible' : 'No students available'}
                </p>
              )}
            </div>
          </ScrollArea>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={onClose}>{language === 'fr' ? 'Annuler' : 'Cancel'}</Button>
            <Button onClick={() => onAdd(selected)} disabled={selected.length === 0} data-testid="confirm-add-members">
              {language === 'fr' ? `Ajouter (${selected.length})` : `Add (${selected.length})`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StudyGroupsPage;
